import { Injectable } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CarrierSolution } from '@shared/interfaces/entities/carrier-solution.interface';
import { Drug } from '@shared/interfaces/entities/drug.interface';
import { DrugCategory } from '@shared/interfaces/entities/drug-category.interface';
import { Product } from '@shared/interfaces/entities/product.interface';
import { EntitiesService } from '@shared/service/entities/entities.service';
import { SnackbarService } from '@shared/service/snackbar/snackbar.service';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { genericErrorAction } from '../application/application.actions';
import {
  carrierSolutions,
  cleanUpTables,
  drugCategories,
  drugs,
  products
} from './entities.actions';

@Injectable()
export class EntitiesEffects {
  drugCategoriesData = createEffect(() =>
    this.actions$.pipe(
      ofType(drugCategories.load),
      switchMap((payload: { searchInput?: string }) =>
        this.entitiesService.getDrugsCategories(payload.searchInput).pipe(
          map((response: Array<DrugCategory>) => {
            return drugCategories.loadSuccess({ drugCategories: response });
          }),
          catchError(() => of(cleanUpTables()))
        )
      )
    )
  );

  deleteDrugCategory = createEffect(() =>
    this.actions$.pipe(
      ofType(drugCategories.delete),
      switchMap((payload: { id: number; searchInput: string; name: string }) =>
        this.entitiesService.deleteDrugCategoryById(payload.id).pipe(
          map(() => {
            this.notificationService.success(
              this.translocoService.translate('deleted', {
                context: this.translocoService.translate('entities.drugCategory'),
                entity: payload.name
              })
            );
            return drugCategories.load({ searchInput: payload.searchInput });
          }),
          catchError(() => of(genericErrorAction({})))
        )
      )
    )
  );

  deleteDrug = createEffect(() =>
    this.actions$.pipe(
      ofType(drugs.delete),
      switchMap((payload: { id: number; searchInput: string; name: string }) =>
        this.entitiesService.deleteDrugById(payload.id).pipe(
          map(() => {
            this.notificationService.success(
              this.translocoService.translate('deleted', {
                context: this.translocoService.translate('entities.drug'),
                entity: payload.name
              })
            );
            return drugs.load({ searchInput: payload.searchInput });
          }),
          catchError(() => of(genericErrorAction({})))
        )
      )
    )
  );

  drugsData = createEffect(() =>
    this.actions$.pipe(
      ofType(drugs.load),
      switchMap((payload: { searchInput?: string }) =>
        this.entitiesService.getDrugs(payload.searchInput).pipe(
          map((response: Array<Drug>) => {
            return drugs.loadSuccess({ drugs: response });
          }),
          catchError(() => of(cleanUpTables()))
        )
      )
    )
  );

  carrierSolutionsData = createEffect(() =>
    this.actions$.pipe(
      ofType(carrierSolutions.load),
      switchMap((payload: { searchInput?: string }) =>
        this.entitiesService.getCarrierSolutions(payload.searchInput).pipe(
          map((response: Array<CarrierSolution>) => {
            return carrierSolutions.loadSuccess({ carrierSolutions: response });
          }),
          catchError(() => of(cleanUpTables()))
        )
      )
    )
  );

  deleteCarrierSolution = createEffect(() =>
    this.actions$.pipe(
      ofType(carrierSolutions.delete),
      switchMap((payload: { id: number; searchInput: string; name: string }) =>
        this.entitiesService.deleteCarrierSolutionById(payload.id).pipe(
          map(() => {
            this.notificationService.success(
              this.translocoService.translate('deleted', {
                context: this.translocoService.translate('entities.carrierSolution'),
                entity: payload.name
              })
            );
            return carrierSolutions.load({ searchInput: payload.searchInput });
          }),
          catchError(() => of(genericErrorAction({})))
        )
      )
    )
  );

  productsData = createEffect(() =>
    this.actions$.pipe(
      ofType(products.load),
      switchMap((payload: { searchInput?: string }) =>
        this.entitiesService.getProducts(payload.searchInput).pipe(
          map((response: Array<Product>) => {
            return products.loadSuccess({ products: response });
          }),
          catchError(() => of(cleanUpTables()))
        )
      )
    )
  );

  deleteProduct = createEffect(() =>
    this.actions$.pipe(
      ofType(products.delete),
      switchMap((payload: { id: number; searchInput: string; name: string }) =>
        this.entitiesService.deleteProductById(payload.id).pipe(
          map(() => {
            this.notificationService.success(
              this.translocoService.translate('deleted', {
                context: this.translocoService.translate('entities.product'),
                entity: payload.name
              })
            );
            return products.load({ searchInput: payload.searchInput });
          }),
          catchError(() => of(genericErrorAction({})))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly entitiesService: EntitiesService,
    private readonly notificationService: SnackbarService,
    private readonly translocoService: TranslocoService
  ) {}
}
